import { useEffect, useRef, useState } from 'react'
import { Link, useSearchParams } from 'react-router-dom'
import { ArrowUp } from 'lucide-react'
import AdvisorRecommendation from './AdvisorRecommendation'
import AdvisorThinking from './AdvisorThinking'
import { hydrateAdvisorLiveData } from '../../knowledge'
import {
  applyChoice,
  completeDiscoverClarification,
  completeDiscoverRecommendation,
  createInitialState,
  startAgain,
  submitOtherQuestion,
} from './conversationEngine'
import { syncLead } from './leadCapture'

function AdvisorActions({ options, onChoose, featured }) {
  if (!options?.length) return null

  return (
    <div
      className={featured ? 'advisor-actions advisor-actions-featured' : 'advisor-actions'}
      role="group"
      aria-label="Conversation choices"
    >
      {options.map((option) => {
        const content = (
          <>
            <span className="advisor-action-label">{option.label}</span>
            {option.support && (
              <span className="advisor-action-support">{option.support}</span>
            )}
          </>
        )

        if (option.href) {
          const external = option.href.startsWith('http')
          if (external) {
            return (
              <a
                key={option.id}
                href={option.href}
                target="_blank"
                rel="noreferrer"
                className="advisor-action-chip block text-left"
              >
                {content}
              </a>
            )
          }

          return (
            <Link
              key={option.id}
              to={option.href}
              className="advisor-action-chip block text-left"
            >
              {content}
            </Link>
          )
        }

        return (
          <button
            key={option.id}
            type="button"
            onClick={() => onChoose(option)}
            className="advisor-action-chip text-left"
          >
            {content}
          </button>
        )
      })}
    </div>
  )
}

export default function AdvisorConversation({ initialCourseCode }) {
  const [searchParams] = useSearchParams()
  const courseCode = initialCourseCode || searchParams.get('course') || searchParams.get('c') || null

  const [state, setState] = useState(() => createInitialState(null, courseCode))
  const [draft, setDraft] = useState('')
  const [keyboardInset, setKeyboardInset] = useState(0)
  const [ready, setReady] = useState(false)
  const [pending, setPending] = useState(false)
  const [pendingVisitor, setPendingVisitor] = useState('')
  const inputRef = useRef(null)
  const endRef = useRef(null)
  const pendingRef = useRef(false)

  useEffect(() => {
    if (courseCode && state.targetCourse?.code !== courseCode) {
      setState(createInitialState(null, courseCode))
    }
  }, [courseCode])

  useEffect(() => {
    let active = true
    hydrateAdvisorLiveData()
      .catch(() => {})
      .finally(() => {
        if (active) setReady(true)
      })
    return () => { active = false }
  }, [])

  useEffect(() => {
    const viewport = window.visualViewport
    if (!viewport) return undefined

    const sync = () => {
      const inset = Math.max(0, window.innerHeight - viewport.height - viewport.offsetTop)
      setKeyboardInset(inset)
    }

    viewport.addEventListener('resize', sync)
    viewport.addEventListener('scroll', sync)
    sync()
    return () => {
      viewport.removeEventListener('resize', sync)
      viewport.removeEventListener('scroll', sync)
    }
  }, [])

  useEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches
    endRef.current?.scrollIntoView({ behavior: reduceMotion ? 'auto' : 'smooth', block: 'end' })
  }, [state.messages, state.recommendation, state.options, state.inputMode, pending, pendingVisitor])

  useEffect(() => {
    if (state.inputMode) {
      const t = setTimeout(() => inputRef.current?.focus(), 350)
      return () => clearTimeout(t)
    }
    return undefined
  }, [state.inputMode])

  const choose = async (option) => {
    if (pendingRef.current) return
    if (option.actionType === 'START_APPLICATION' || option.id === 'register') {
      syncLead(state, { message: 'I am ready to register.', action: 'application_started' })
    }
    if (option.href) return

    const needsRecommendation = state.step === 'discover_experience' || state.step === 'discover_clarify'
    if (needsRecommendation) {
      pendingRef.current = true
      setPending(true)
      setPendingVisitor(option.label || '')
      try {
        const next = state.step === 'discover_clarify'
          ? await completeDiscoverClarification(state, option)
          : await completeDiscoverRecommendation(state, option)
        setState(next)
      } catch {
        setState((current) => ({
          ...current,
          messages: [
            ...current.messages,
            { id: `m-${current.nextId}`, role: 'visitor', text: option.label },
            {
              id: `m-${current.nextId + 1}`,
              role: 'advisor',
              text: "I'm having a little trouble connecting right now. You can still contact SMA Admissions directly and we'll be happy to help.",
            },
          ],
          nextId: current.nextId + 2,
          step: 'followup',
          options: [{ id: 'contact', label: 'Talk to Admissions', href: '/contact' }],
          inputMode: true,
          recommendation: null,
        }))
      } finally {
        pendingRef.current = false
        setPending(false)
        setPendingVisitor('')
      }
      return
    }

    setState((current) => applyChoice(current, option))
  }

  const sendOther = async (event) => {
    event.preventDefault()
    const text = draft.trim()
    if (!text || pendingRef.current) return
    pendingRef.current = true
    setPending(true)
    setPendingVisitor(text)
    setDraft('')
    try {
      const next = await submitOtherQuestion(state, text)
      setState(next)
    } catch {
      setState((current) => ({
        ...current,
        messages: [
          ...current.messages,
          { id: `m-${current.nextId}`, role: 'visitor', text },
          {
            id: `m-${current.nextId + 1}`,
            role: 'advisor',
            text: "I'm having a little trouble connecting right now. You can still contact SMA Admissions directly and we'll be happy to help.",
          },
        ],
        nextId: current.nextId + 2,
        step: 'followup',
        options: [{ id: 'contact', label: 'Talk to Admissions', href: '/contact' }],
        inputMode: true,
      }))
    } finally {
      pendingRef.current = false
      setPending(false)
      setPendingVisitor('')
    }
  }

  const featuredActions = state.step === 'topic' || state.step === 'discover_interest'
    || state.step === 'discover_goal' || state.step === 'discover_experience'
    || state.step === 'discover_clarify'
    || state.step === 'fees_area' || state.step === 'learn_area'
    || state.step === 'careers_area' || state.step === 'human'
    || state.step === 'followup' || state.step === 'ai' || state.step === 'capture_offer'
    || state.step === 'course_inquiry'

  const capturing = state.step === 'capture_name' || state.step === 'capture_phone' || state.step === 'human_lead'
  const showInput = state.inputMode || state.step === 'ai' || capturing

  return (
    <div className="flex min-h-0 flex-1 flex-col">
      <div className="advisor-thread min-h-0 flex-1 overflow-y-auto overflow-x-hidden px-5 sm:px-8">
        <div
          className={`max-w-2xl mx-auto pt-10 sm:pt-16 pb-[max(2rem,env(safe-area-inset-bottom))] transition-all duration-700 ${
            ready ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4'
          }`}
          aria-live="polite"
          aria-busy={pending}
        >
          {state.messages.map((message, index) => {
            const previous = state.messages[index - 1]
            const grouped = previous && previous.role === message.role

            return (
              <div
                key={message.id}
                className={`advisor-msg ${message.role === 'visitor' ? 'advisor-msg-visitor' : 'advisor-msg-advisor'} ${
                  grouped ? 'advisor-msg-grouped' : ''
                }`}
                style={{ animationDelay: `${Math.min(index, 6) * 80}ms` }}
              >
                {message.role === 'visitor' ? (
                  <p className="font-display text-xl sm:text-2xl font-light text-cinema-gold leading-snug">
                    {message.text}
                  </p>
                ) : (
                  <p className={`font-display font-light text-white leading-snug ${
                    index === 0 && state.step === 'topic'
                      ? 'text-2xl sm:text-3xl lg:text-4xl'
                      : 'text-xl sm:text-2xl lg:text-[1.7rem]'
                  }`}
                  >
                    {message.text}
                  </p>
                )}
              </div>
            )
          })}

          {pendingVisitor ? (
            <div className="advisor-msg advisor-msg-visitor">
              <p className="font-display text-xl sm:text-2xl font-light text-cinema-gold leading-snug">
                {pendingVisitor}
              </p>
            </div>
          ) : null}

          {pending ? <AdvisorThinking /> : null}

          {state.step === 'recommend' && state.recommendation && !pending && (
            <AdvisorRecommendation
              course={state.recommendation}
              answers={state.answers}
              reason={state.answers.recommendationReason}
              nextStep={state.answers.recommendationNextStep}
              conversationId={state.conversationId}
              onStartApplication={() => syncLead(state, { message: 'I am ready to register.', action: 'application_started' })}
              onTalkToHuman={() => choose({ id: 'human', label: 'Talk to Admissions' })}
              onStartAgain={() => setState(startAgain(state))}
            />
          )}

          {state.options.length > 0 && !pending && (
            <AdvisorActions
              options={state.options}
              onChoose={choose}
              featured={featuredActions}
            />
          )}

          <div ref={endRef} />
        </div>
      </div>

      {showInput && (
        <div
          className="advisor-dock advisor-dock-open shrink-0"
          style={{ paddingBottom: `max(0.75rem, calc(env(safe-area-inset-bottom) + ${keyboardInset}px))` }}
        >
          <div className="max-w-2xl mx-auto px-5 sm:px-8 pt-3">
            <form onSubmit={sendOther} className="flex items-end gap-2">
              <label htmlFor="advisor-other" className="sr-only">
                {state.step === 'capture_name' ? 'Your name' : state.step === 'capture_phone' || state.step === 'human_lead' ? 'WhatsApp number' : 'Your question'}
              </label>
              <input
                id="advisor-other"
                ref={inputRef}
                value={draft}
                onChange={(event) => setDraft(event.target.value)}
                onFocus={() => endRef.current?.scrollIntoView({ block: 'end' })}
                placeholder={state.step === 'capture_name' ? 'Your name' : state.step === 'capture_phone' || state.step === 'human_lead' ? 'WhatsApp number' : 'Type your question'}
                autoComplete={state.step === 'capture_name' ? 'name' : state.step === 'capture_phone' || state.step === 'human_lead' ? 'tel' : 'off'}
                className="advisor-input"
              />
              <button
                type="submit"
                disabled={!draft.trim() || pending}
                className="advisor-send"
                aria-label={pending ? 'Waiting for advisor response' : capturing ? 'Send details' : 'Send question'}
              >
                <ArrowUp className="w-5 h-5" />
              </button>
            </form>
          </div>
        </div>
      )}
    </div>
  )
}
